<template>
    <div id="app" :class="componentClasses">
        <widget-state v-if="assistent">
            <template #minimized>
                <floating-beam v-if="WidgetType.FLOATING_BEAM.equals(typeForWidget)" />
                <open-button v-else />
            </template>

            <template #opened>
                <chat-container>
                    <chat-header>
                        <template v-if="chat.handoverAcceptedBy.value === null">
                            <avatar-icon
                                v-if="isChatStyleFlat"
                            />
                            <avatar-icon
                                v-else
                                smaller
                                round
                            />
                        </template>
                        <template v-else>
                            <employee-avatar-icon v-if="isChatStyleFlat" :user="chat.handoverAcceptedBy.value" />
                            <employee-avatar-icon
                                v-else
                                :user="chat.handoverAcceptedBy.value"
                                smaller
                                round
                            />
                        </template>
                        <div style="flex-grow: 2">
                            <template v-if="chat.handoverAcceptedBy.value === null">
                                <h4>{{ assistent?.name }}</h4>
                                <div class="assistent-title">
                                    <div :class="chipClasses">
                                        {{ t('chip') }}
                                    </div>
                                    {{ t('title') }}
                                </div>
                            </template>
                            <template v-else>
                                <h4>{{ chat.handoverAcceptedBy.value.name.firstName }}</h4>
                                <div class="assistent-title">
                                    <div v-if="isChatStyleModern" class="chip">
                                        <user-headset-icon class="chip__icon" />
                                    </div>
                                    {{ t('live') }}
                                </div>
                            </template>
                        </div>
                        <mobile-minimize-button />
                    </chat-header>

                    <chat-content />

                    <chat-quick-options
                        v-if="!chat.hasUserMessage.value"
                        :quick-options="assistent.widget.quickOptions"
                    />

                    <restart-chat-button v-if="chat.isClosed.value" />
                    <chat-input v-else />

                    <chat-footer />

                    <transition
                        name="slide"
                        appear
                        mode="out-in"
                    >
                        <lost-connection-overlay v-if="chat.lostConnection.value" />
                    </transition>

                    <transition
                        name="slide"
                        appear
                        mode="out-in"
                    >
                        <start-chat-overlay v-if="showStartChatOverlay" />
                    </transition>

                    <transition
                        name="slide"
                        appear
                        mode="out-in"
                    >
                        <handover-overlay v-if="chat.showHandoverOverlay.value" />
                    </transition>
                    <transition
                        name="slide"
                        appear
                        mode="out-in"
                    >
                        <privacy-statement-overlay v-if="showPrivacyStatementOverlay" />
                    </transition>
                </chat-container>
            </template>
        </widget-state>
    </div>
</template>

<script setup lang="ts">
    import { computed, onMounted, onUnmounted } from 'vue';
    import useConfiguration from '@/composables/useConfiguration';
    import useWidgetState, { State } from '@/composables/useWidgetState';
    import WidgetState from '@/components/WidgetState.vue';
    import OpenButton from '@/components/buttons/OpenButton.vue';
    import ChatContainer from '@/components/ChatContainer.vue';
    import ChatHeader from '@/components/ChatHeader.vue';
    import ChatContent from '@/components/ChatContent.vue';
    import ChatInput from '@/components/ChatInput.vue';
    import AvatarIcon from '@/components/avatar/AvatarIcon.vue';
    import ChatFooter from '@/components/ChatFooter.vue';
    import StartChatOverlay from '@/components/overlays/StartChatOverlay.vue';
    import PrivacyStatementOverlay from '@/components/overlays/PrivacyStatementOverlay.vue';
    import MobileMinimizeButton from '@/components/buttons/MobileMinimizeButton.vue';
    import ChatQuickOptions from '@/components/ChatQuickOptions.vue';
    import { Assistent, WidgetType } from '@keyvue/one-api-client';
    import { useI18n } from 'vue-i18n';
    import messages from './App.messages.json';
    import useWidgetAppearance from '@/composables/useWidgetAppearance';
    import FloatingBeam from '@/components/FloatingBeam.vue';
    import Chat from '@/chat/Chat';
    import LostConnectionOverlay from '@/components/overlays/LostConnectionOverlay.vue';
    import HandoverOverlay from '@/components/overlays/HandoverOverlay.vue';
    import RestartChatButton from '@/components/buttons/RestartChatButton.vue';
    import EmployeeAvatarIcon from '@/components/avatar/EmployeeAvatarIcon.vue';
    import UserHeadsetIcon from '@keyvue/one-awesome-icons/icons/regular/user-headset.svg?component';
    import useVisitorData from '@/composables/useVisitorData';

    const { load, updateAssistent, assistent, isLoaded, isPreview } = useConfiguration();
    const { gatherVisitorData } = useVisitorData();
    const { showStartChatOverlay, showPrivacyStatementOverlay, state } = useWidgetState();
    const { locale, availableLocales } = useI18n();
    const { t } = useI18n({ messages });
    const { isChatStyleFlat, isChatStyleModern, typeForWidget } = useWidgetAppearance();

    const chat = Chat.getInstance();

    const componentClasses = computed(() => ({
        'app--flat': isChatStyleFlat.value,
        'app--modern': isChatStyleModern.value,
    }));

    const chipClasses = computed(() => ({
        'chip': true,
        'chip--flat': isChatStyleFlat.value,
    }));

    function sendMessage(type: string, payload: Record<string, any> = {}) {
        window.parent?.postMessage({
            type,
            payload,
        }, '*'); // @todo dicht timmeren
    }

    function messageHandler(event: MessageEvent) {
        const { type, payload } = event.data;

        if (type === 'update-assistent' && payload.assistent !== null) {
            updateAssistent(
                Assistent.fromObject(
                    JSON.parse(payload.assistent),
                ),
            );
        }
    }

    const enabledLocales = computed(() => {
        return availableLocales.filter((availableLocale) => {
            if (availableLocale === 'en') {
                return assistent.value?.behavior.englishSupportEnabled;
            }

            return true;
        });
    });

    function setPreferredLocale() {
        const browserLocales = navigator.languages.map((l) => l.split('-')[0]);

        for (let i = 0; i < browserLocales.length; i += 1) {
            const l = browserLocales[i];
            if (enabledLocales.value.includes(l)) {
                locale.value = l;
                return;
            }
        }
    }

    onMounted(async () => {
        await load();

        if (!assistent.value) {
            return;
        }

        await chat.initialize(
            assistent.value.id.id,
            assistent.value.widget.greeting ?? '',
            isPreview.value,
        );

        setPreferredLocale();

        if (chat.isStarted.value) {
            // skip showing start chat message
            showStartChatOverlay.value = false;
        }

        // handle preview mode
        if (isPreview.value) {
            // skip minimization
            state.value = State.Opened;

            // skip showing start chat message
            showStartChatOverlay.value = false;

            // if in iframe (which is only in preview)
            if (window.parent !== window) {
                window.addEventListener('message', messageHandler);
                sendMessage('ready');
            }

            return;
        }

        await gatherVisitorData(assistent.value?.id.value);

        // Everything is loaded, so we can show the widget
        if (isLoaded.value) {
            state.value = State.Minimized;
        }
    });

    onUnmounted(() => {
        if (!isPreview.value) {
            return;
        }

        window.removeEventListener('message', messageHandler);
    });
</script>

<style lang="scss">
    @import "assets/scss/_reset.scss";

    :host,
    :root {
        --color-widget: #007bff;
        --color-widget-text: #fff;
        --color-balloon: var(--color-widget);
    }

    #app {
        all: initial;
        font-family: Roboto, sans-serif;
        color: #333;
        line-height: 1.5;
        margin: 0;
        padding: 0;

        button {
            font-size: 16px;
        }

        .slide-enter-active {
            animation: slideIn 300ms ease;
        }

        .slide-leave-active {
            animation: slideIn 300ms ease reverse;
        }

        @keyframes slideIn {
            from {
                transform: translateY(100%);
            }
            to {
                transform: translateY(0);
            }
        }
    }

    .assistent-title {
        flex-direction: row;
        display: flex;
        gap: 5px;
        align-items: center
    }

    .chip {
        width: 18px;
        height: 18px;
        background-color: var(--color-widget);
        color: var(--color-widget-text);
        border-radius: 6px;
        font-size: 12px;
        display: flex;
        justify-content: center;
        align-items: center;

        &--flat {
            background-color: var(--color-widget-text);
            color: var(--color-widget);
        }

        &__icon {
            width: 12px;
            height: 12px;
        }
    }
</style>
